import { createI18n } from 'vue-i18n'

const fileNameToLocaleModuleDict = import.meta.glob<{ default: Record<string, string> }>('./locales/*.json', {
  eager: true,
})

/**
 * The locales a user may actually choose, as a fixed, named list.
 *
 * ⚠️ Deliberately NOT derived from the glob above. That glob still loads every
 * file in ./locales/ — including br.json, cn.json, es.json and ir.json, which
 * are leftovers of the Vuestic template. A list built from the loaded messages
 * would therefore offer Portuguese, Spanish, Chinese and Persian in the menu,
 * none of which this project maintains. The glob keeps loading them (removing
 * the files is the customer's decision, not this change's), but only what is
 * listed here can be selected.
 *
 * `code` is the locale key, which is the locale FILE NAME without .json —
 * English lives in gb.json, so its code is 'gb', not 'en'.
 * `labelKey` is an i18n key, so the menu entry is translated like any other
 * interface text.
 */
export const SUPPORTED_LOCALES = [
  { code: 'de', labelKey: 'language.german' },
  { code: 'gb', labelKey: 'language.english' },
] as const

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]['code']

export const DEFAULT_LOCALE: SupportedLocale = 'de'

const messages: { [P: string]: Record<string, string> } = {}
Object.entries(fileNameToLocaleModuleDict)
  .map(([fileName, localeModule]) => {
    const fileNameParts = fileName.split('/')
    const fileNameWithoutPath = fileNameParts[fileNameParts.length - 1]
    const localeName = fileNameWithoutPath.split('.json')[0]

    return [localeName, localeModule.default] as const
  })
  .forEach((localeNameLocaleMessagesTuple) => {
    messages[localeNameLocaleMessagesTuple[0]] = localeNameLocaleMessagesTuple[1]
  })

export default createI18n({
  legacy: false,
  // The start value only. A locale stored for this user is applied in main.ts
  // (see ./locale.ts) — it cannot be read here, because this module is
  // evaluated at import time, before Pinia is active.
  locale: DEFAULT_LOCALE,
  fallbackLocale: 'de',
  messages,
  // ⚠️ No 'gb' block on purpose. With fallbackLocale 'de', $n(value, 'decimal')
  // under locale 'gb' resolves the format from 'de' and keeps printing amounts
  // German-style (1.234,50). A 'gb' block would switch roughly a thousand
  // amount displays to 1,234.50 — and ArticleOverview.vue:192 feeds $n() output
  // back into $fmt.num(), which parses German notation and would misread it.
  // Number formatting is therefore left out of this change.
  numberFormats: {
    "de": {
      "decimal": {
        "style": "decimal",
        "minimumFractionDigits": 2,
        "maximumFractionDigits": 2
      },
      "int": {
        "style": "decimal",
        "minimumFractionDigits": 0,
        "maximumFractionDigits": 0
      },
      "percent": {
        "style": 'percent', "useGrouping": false
      }
    },
    "en": {
      "decimal": {
        "style": "decimal",
        "minimumFractionDigits": 2,
        "maximumFractionDigits": 2
      },
      "int": {
        "style": "decimal",
        "minimumFractionDigits": 0,
        "maximumFractionDigits": 0
      },
      "percent": {
        "style": 'percent', "useGrouping": false
      }
    }
  }
})
